import { Signal, WritableSignal, computed, signal } from '@angular/core';
import moment from 'moment';
import { PlatformIChingConsultationDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-dto';
import { Notifier } from '@shared/notifier/types';
import { IChingCastMode, IChingCoinFace } from '../core/iching.models';
import { HdIChingJournalService, IChingJournalUpdate } from '../journal/hd-iching-journal.service';
import { parseCoinFaces, parseLines } from '../journal/hd-iching-journal.util';
import { EMPTY_ICHING_DRAFT, IChingDraft } from './hd-iching-draft';
import { IChingTranslate } from './iching-consult-translate';

const CAST_MODES: readonly IChingCastMode[] = ['coins', 'manual', 'direct'];

export interface IChingConsultationSessionDeps {
  readonly journal: Pick<HdIChingJournalService, 'create' | 'update'>;
  readonly notifier: Pick<Notifier, 'success' | 'error'>;
  readonly t: IChingTranslate;
  /** Het concept van de pagina; de bewaarde raadpleging (`savedId` e.a.) leeft erin. */
  readonly draft: WritableSignal<IChingDraft>;
  /** Worpmodus van het scherm (de setting): die gaat mee bij een nieuwe raadpleging. */
  readonly castMode: Signal<IChingCastMode>;
}

/**
 * De raadpleging tegenover het dagboek: bewaren (nieuw → create, al bewaard → update), vandaag
 * herlezen, heropenen uit het dagboek en vergeten na verwijderen. Id, `revisitedOn` en gekoppelde
 * chart van de bewaarde raadpleging staan in het concept (localStorage), zodat een herlaad geen
 * tweede raadpleging maakt. `saved` (bewaard en sindsdien ongewijzigd) is vluchtig.
 */
export class HdIChingConsultationSession {
  private readonly savingState = signal(false);
  private readonly savedState = signal(false);
  private readonly refreshState = signal(0);
  private readonly lastSavedState = signal<PlatformIChingConsultationDto | undefined>(undefined);

  readonly saving = this.savingState.asReadonly();
  readonly saved = this.savedState.asReadonly();
  /** De raadpleging zoals het dagboek ze na de laatste geslaagde bewaarbeurt teruggaf. */
  readonly lastSaved = this.lastSavedState.asReadonly();
  readonly savedId: Signal<string | undefined>;
  /** Verhoogt na elke geslaagde bewaarbeurt: de dagboeklijst herlaadt. */
  readonly journalRefresh = this.refreshState.asReadonly();

  constructor(private readonly deps: IChingConsultationSessionDeps) {
    this.savedId = computed(() => deps.draft().savedId);
  }

  /** Het concept wijkt af van wat bewaard is. */
  markDirty(): void {
    this.savedState.set(false);
  }

  /** Zet het concept op een raadpleging uit het dagboek; bewaren werkt die daarna bij. */
  reopen(dto: PlatformIChingConsultationDto): boolean {
    if (!dto.id) {
      return false;
    }
    this.deps.draft.set({
      ...EMPTY_ICHING_DRAFT,
      question: dto.question ?? '',
      questionKind: dto.questionKind === 'situation' ? 'situation' : 'question',
      castMode: CAST_MODES.find(mode => mode === dto.castMode) ?? EMPTY_ICHING_DRAFT.castMode,
      lines: parseLines(dto.lines),
      // Muntzijden terug voor het worp-overzicht en de herkomst van de bewegende lijnen.
      coinFaces: parseCoinFaces(dto.coinFaces),
      keywords: dto.keywords ?? [],
      notes: dto.notes ?? '',
      doList: dto.doList ?? [],
      dontList: dto.dontList ?? [],
      nameRevealed: true,
      consultedAt: dto.consultedAt ?? undefined,
      linkedChartId: dto.linkedChartId ?? undefined,
      savedId: dto.id,
      savedRevisitedOn: dto.revisitedOn ?? undefined,
    });
    this.savedState.set(true);
    return true;
  }

  /** Na verwijderen uit het dagboek: is dat de open raadpleging, dan is ze niet meer bewaard. */
  forget(id: string): void {
    if (this.deps.draft().savedId !== id) {
      return;
    }
    this.deps.draft.update(draft => withoutSaved(draft));
    this.savedState.set(false);
  }

  /** Bewaart in het dagboek, of werkt de al bewaarde raadpleging bij; het concept blijft staan. */
  async save(): Promise<void> {
    await this.persist(this.deps.draft().savedRevisitedOn, 'humanDesign.iching.practice.saved');
  }

  /** Markeert de bewaarde raadpleging als vandaag herlezen, met de huidige reflectie. */
  async revisit(): Promise<void> {
    if (this.savedId() === undefined) {
      return;
    }
    await this.persist(moment().toISOString(), 'humanDesign.iching.practice.revisited');
  }

  /** Nieuw → create; al bewaard → update (vervangt alle reflectievelden, dus herlezen en chart mee). */
  private async persist(revisitedOn: string | undefined, successKey: string): Promise<void> {
    const { journal, notifier, t } = this.deps;
    const draft = this.deps.draft();
    if (draft.lines.length !== 6 || this.savingState()) {
      return;
    }
    this.savingState.set(true);
    try {
      const savedId = draft.savedId;
      const reflection: IChingJournalUpdate = {
        question: draft.question,
        keywords: draft.keywords,
        notes: draft.notes,
        doList: draft.doList,
        dontList: draft.dontList,
        revisitedOn,
        linkedChartId: draft.linkedChartId,
        consultedAt: draft.consultedAt,
      };
      const castMode = this.deps.castMode();
      const result =
        savedId === undefined
          ? await journal.create({
              ...reflection,
              lines: draft.lines,
              // Alleen een volledige muntworp; als letters (H/T) zodat de zes worpen in de backendlimiet passen.
              coinFaces:
                castMode === 'coins' && draft.coinFaces.length === 6
                  ? draft.coinFaces.map(faces => faces.map(coinLetter).join(''))
                  : undefined,
              questionKind: draft.questionKind,
              castMode,
            })
          : await journal.update(savedId, reflection);
      const id = result.id ?? savedId;
      // Alleen als het concept nog dezelfde worp heeft: anders is het intussen een andere raadpleging.
      this.deps.draft.update(current =>
        current.lines.join() !== draft.lines.join()
          ? current
          : {
              ...current,
              savedId: id,
              savedRevisitedOn: result.revisitedOn ?? revisitedOn,
              linkedChartId: result.linkedChartId ?? draft.linkedChartId,
              consultedAt: result.consultedAt ?? draft.consultedAt,
            },
      );
      this.lastSavedState.set(result);
      this.savedState.set(true);
      this.refreshState.update(token => token + 1);
      notifier.success(t(successKey));
    } catch {
      notifier.error(t('humanDesign.iching.practice.saveError'));
    } finally {
      this.savingState.set(false);
    }
  }
}

/** Concept zonder de velden van een bewaarde raadpleging; chart en tijdstip blijven staan. */
export function withoutSaved(draft: IChingDraft): IChingDraft {
  const { savedId: _id, savedRevisitedOn: _revisited, ...rest } = draft;
  return rest;
}

/** Kop = H, munt = T, zoals het dagboek muntzijden bewaart ("HHT"). */
function coinLetter(face: IChingCoinFace): string {
  return face === 'head' ? 'H' : 'T';
}
